import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type { ProductDisplay } from "@/lib/api/dutchie"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Cleans up filter options by removing any that don't have matching products
 * @param filters - Object containing filter options (key-value pairs)
 * @param products - Array of products to check against
 * @param filterType - Type of filter (e.g. 'types', 'concentrates', 'effects')
 * @returns Cleaned up filters with only options that have matching products
 */

/**
 * Extract THC or CBD numeric value from a ProductDisplay.
 * Looks at both string fields (e.g. "18%"), potency objects (potencyThc.formatted),
 * and falls back to regex search in name/description. Returns 0 if nothing found.
 */
export function extractPotencyValue(product: ProductDisplay, type: 'thc' | 'cbd'): number {
  // Direct numeric in string fields, e.g. "18%"
  const direct = type === 'thc' ? product.thc : product.cbd
  if (direct) {
    const m = direct.match(/(\d+(?:\.\d+)?)/)
    if (m?.[1]) return parseFloat(m[1])
  }

  // Dutchie potency objects
  const potObj: any = type === 'thc' ? (product as any).potencyThc : (product as any).potencyCbd
  if (potObj?.formatted) {
    const m = potObj.formatted.match(/(\d+(?:\.\d+)?)/)
    if (m?.[1]) return parseFloat(m[1])
  }
  if (potObj?.range?.length) {
    return potObj.range[0]
  }

  // Fallback – search text
  const text = `${product.name ?? ''} ${product.description ?? ''}`.toLowerCase()
  const unit = type === 'thc' ? '%' : '%'
  const regex = new RegExp(`${type}:?\\s*(\\d+(?:\\.\\d+)?)\\s*${unit}`, 'i')
  const match = text.match(regex)
  if (match?.[1]) return parseFloat(match[1])

  return 0
}

/**
 * Normalize a raw weight string (e.g. "0.5 g", "1gram") ➜ ".5g" / "1g" etc.
 */
export function normalizeWeight(weight: string): string {
  if (!weight) return weight
  let w = weight.toLowerCase().replace(/\s+/g, '')
  // Ensure trailing g
  if (!w.endsWith('g')) w += 'g'
  // Collapse leading 0.x ➜ .5g
  w = w.replace(/^0\./, '.')
  return w
}

export function cleanUpFilters(
  filters: Record<string, boolean>,
  products: ProductDisplay[],
  filterType: string
): Record<string, boolean> {
  // Create a new object to store only filters with matching products
  const cleanedFilters: Record<string, boolean> = {};
  
  // Iterate through each filter option
  Object.keys(filters).forEach(filterOption => {
    // Check if any product matches this filter option
    const hasMatchingProduct = products.some(product => {
      const filterOptionLower = filterOption.toLowerCase();
            // Match based on filter type with very strict matching
      switch(filterType) {
        case 'types': // Strain types with support for hybrid variations
          // Get the normalized strain type for this product
          const lowerStrainType = (product.strainType || '').toLowerCase();
          let normalizedType = '';
          
          // Determine the normalized strain type using the same logic as in pre-rolls page
          if (lowerStrainType.includes('sativa') && lowerStrainType.includes('hybrid')) {
            normalizedType = 'sativa hybrid';
          } else if (lowerStrainType.includes('indica') && lowerStrainType.includes('hybrid')) {
            normalizedType = 'indica hybrid';
          } else if (lowerStrainType.includes('hybrid')) {
            normalizedType = 'hybrid';
          } else if (lowerStrainType.includes('sativa')) {
            normalizedType = 'sativa';
          } else if (lowerStrainType.includes('indica')) {
            normalizedType = 'indica';
          }
          
          // Match the normalized type against the filter option
          return normalizedType === filterOptionLower;
          
        case 'weights': // Product weights
          // Exact matching for weights
          return product.option?.toLowerCase().includes(filterOptionLower);
          
        case 'subcategories': // Product subcategories
          // Exact match for subcategory
          return product.subcategory?.toLowerCase() === filterOptionLower;
          
        case 'effects': // Product effects
          // Check for exact word match in description or effects
          const effectRegex = new RegExp("\\b" + filterOptionLower + "\\b", 'i');
          return (
            (product.description && effectRegex.test(product.description.toLowerCase())) ||
            (product.effects?.some((effect: string) => effect.toLowerCase() === filterOptionLower))
          );
          
        case 'brands': // Product brands
          return product.brand?.toLowerCase() === filterOptionLower;
          
        case 'concentrates': // Concentrate types
        case 'concentrateTypes': // Concentrate types
          // Much stricter matching for concentrate types - must match exact words
          const wordRegex = new RegExp("\\b" + filterOptionLower + "\\b", 'i');
          
          // Only count exact word matches in name, description, or subcategory
          const nameMatch = product.name && wordRegex.test(product.name.toLowerCase());
          const descMatch = product.description && wordRegex.test(product.description.toLowerCase());
          const subCatMatch = product.subcategory && (product.subcategory.toLowerCase() === filterOptionLower);
            
          return nameMatch || descMatch || subCatMatch;
          
        default: // For other filters, check multiple fields with word boundaries
          const defaultRegex = new RegExp("\\b" + filterOptionLower + "\\b", 'i');
          return (
            (product.name && defaultRegex.test(product.name.toLowerCase())) ||
            (product.description && defaultRegex.test(product.description.toLowerCase())) ||
            (product.subcategory?.toLowerCase() === filterOptionLower)
          );
      }
    });
    
    // Only include this filter option if it has matching products
    if (hasMatchingProduct) {
      cleanedFilters[filterOption] = filters[filterOption];
    }
  });
  
  return cleanedFilters;
}
